import { useSyncExternalStore } from "react";

/**
 * ChatBar prefixes every user turn with `[Current Page: /path]` so the model
 * knows where the user is. That prefix is plumbing, not something anyone
 * typed, so it is removed before a stored turn is shown or compared.
 */
export function stripPageContext(content: string): string {
	return content.replace(/^\[Current Page: [^\]]*\]\s*/, "");
}

/**
 * What the assistant's page context carries after a close's project address,
 * so it knows the project is a close: `/workflow/projects/50?kind=close`.
 * Assistant metadata only: it is never in the browser's address.
 *
 * 🪤 reconciliation-ai matches this string exactly. Change it there too.
 */
export const CLOSE_PAGE_MARKER = "?kind=close";

/**
 * A close's project page, telling the assistant what it is. Bound to the
 * address and the project it was registered for, so a hint left over from
 * another page is never used.
 */
export interface ClosePageHint {
	pathname: string;
	projectId: number;
	/** The close's client, for the chat transport: the address names none. */
	companyId: number | null;
}

let current: ClosePageHint | null = null;
const listeners = new Set<() => void>();

function publish(next: ClosePageHint | null): void {
	current = next;
	for (const listener of listeners) listener();
}

/**
 * Register the open close's hint. Returns the way to clear it, which leaves
 * a hint registered since (another close's page) alone.
 */
export function registerClosePageHint(hint: ClosePageHint): () => void {
	publish(hint);
	return () => {
		if (current === hint) publish(null);
	};
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/** The hint registered right now, whichever page it is for. */
export function getClosePageHint(): ClosePageHint | null {
	return current;
}

/** `getClosePageHint`, for a component: it re-renders when the hint changes. */
export function useClosePageHint(): ClosePageHint | null {
	return useSyncExternalStore(subscribe, getClosePageHint);
}

/** The id a close's project address may carry: 1 to 12 digits. */
const PROJECT_ADDRESS = /^\/workflow\/projects\/(\d{1,12})$/;

/** The hint, only when it was registered for exactly this address. */
function hintFor(
	pathname: string,
	hint: ClosePageHint | null | undefined,
): ClosePageHint | null {
	if (!hint || hint.pathname !== pathname) return null;
	const id = PROJECT_ADDRESS.exec(pathname)?.[1];
	return id !== undefined && id === String(hint.projectId) ? hint : null;
}

/**
 * The `[Current Page: ...]` prefix of a user turn. On a close's project page,
 * once the page has said so, the address carries the close marker; anywhere
 * else, and for a hint registered for another address, it is the plain path.
 */
export function buildChatPageContext(
	pathname: string,
	hint?: ClosePageHint | null,
): string {
	const marker = hintFor(pathname, hint) ? CLOSE_PAGE_MARKER : "";
	return `[Current Page: ${pathname}${marker}]`;
}

/**
 * The company the open close belongs to, for the chat transport. It comes
 * from the page's project, never from the marker text, and only with a hint
 * that is this address's.
 */
export function chatCompanyIdOf(
	pathname: string,
	hint?: ClosePageHint | null,
): number | null {
	return hintFor(pathname, hint)?.companyId ?? null;
}
